import React, { forwardRef, useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";

interface FloatingLabelMonthPickerProps {
  label?: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  disabled?: boolean;
  error?: string;
  touched?: boolean;
  required?: boolean;
  minYear?: number;
  maxYear?: number;
  style?: React.CSSProperties;
}

const FloatingLabelMonthPicker: React.FC<FloatingLabelMonthPickerProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  error,
  touched,
  required = false,
  minYear = 2026,
  maxYear = 2126,
  style,
}) => {
  const datePickerRef = useRef<DatePicker | null>(null);
  const [isYearView, setIsYearView] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (datePickerRef.current) {
        (datePickerRef.current as any).setOpen(false);
      }
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, []);

  const CustomInput = forwardRef<HTMLInputElement, any>(
    ({ value, onClick }, ref) => (
      <div className="form-floating position-relative" style={style}>
        <input
          ref={ref}
          type="text"
          className={`form-control ${touched && error ? "is-invalid" : ""}`}
          value={value}
          onClick={onClick}
          placeholder=" "
          readOnly
          disabled={disabled}
          style={{
            paddingRight: "40px",
            cursor: disabled ? "not-allowed" : "pointer",
            backgroundColor: disabled ? "#e9ecef" : "white",
          }}
        />

        <label>
          {label}
          {!disabled && required && <span style={{ color: "red" }}> *</span>}
        </label>

        <span
          className="material-icons material-symbols-outlined"
          style={{
            position: "absolute",
            right: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            cursor: !disabled ? "pointer" : "not-allowed",
            color: "#6c757d",
          }}
          onClick={!disabled ? onClick : undefined}
        >
          calendar_month
        </span>

        {touched && error && <div className="invalid-feedback">{error}</div>}
      </div>
    ),
  );

  CustomInput.displayName = "CustomMonthInput";

  const START_YEAR = 2020;
  const currentYear = new Date().getFullYear();
  const totalYears = currentYear - START_YEAR + 1;

  return (
    <DatePicker
      ref={datePickerRef}
      selected={value}
      onChange={(date) => {
        onChange(date);
        setIsYearView(false); // go back to month view
      }}
      onCalendarOpen={() => setIsYearView(false)}
      customInput={<CustomInput />}
      dateFormat="MMM yyyy"
      /* Picker modes */
      showYearPicker={isYearView}
      showMonthYearPicker={!isYearView}
      /* Exact allowed range */
      minDate={new Date(START_YEAR, 0, 1)}
      maxDate={new Date(currentYear, 11, 31)}
      /* Year list size */
      yearItemNumber={totalYears}
      /*  Anchor correctly */
      openToDate={value ?? new Date(currentYear, 0, 1)}
      /*  Click year header to switch */
      renderCustomHeader={({ date }) => (
        <div
          style={{
            textAlign: "center",
            fontWeight: 600,
            cursor: "pointer",
          }}
          onClick={() => setIsYearView(true)}
        >
          {date.getFullYear()}
        </div>
      )}
    />
  );
};

export default FloatingLabelMonthPicker;
